import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  Inject,
} from "@angular/core";
import { MatDialog, MatSnackBar, MatSort } from "@angular/material";
import { InvoiceStatus } from "@mandae/catalogo";
import {
  CustomerApiService,
  CustomerInvoice,
  InvoiceSearchParameters,
} from "src/app/api";
import {merge, Observable, of as observableOf, Subject} from "rxjs";
import { catchError, startWith, switchMap, tap } from "rxjs/operators";
import {
  ListingSearchFilter,
  ListingSearchType,
} from "../../dashboard/interfaces/listing-search-filter";
import { ListingComponent } from "../../dashboard/listing/listing.component";
import downloadFile from "../../download-file";
import { UiRouterState } from "../../services/ajs-router-state.service";
import { StateParamsService } from "../../services/ajs-state-params.service";
import { TourService } from "../../tour/tour.service";
import {HttpClient, HttpParams} from "@angular/common/http";
import { PerfilService } from "src/app/services/ajs-perfil.service";
import { FinancialApiService as Financeiro } from "src/app/services/financial.service";
import { environment } from "./../../../environments/environment";
import {LocalStorageService} from "../../services/localstorage.service";
import { PaymentProofDialogComponent } from "./payment-proof-dialog/payment-proof-dialog.component";
import {
  TemporaryReleaseEligibility,
  TemporaryReleaseResult,
  TemporaryReleaseService,
} from "./temporary-release.service";

@Component({
  selector: "mandae-invoice-list",
  templateUrl: "./invoice-list.component.html",
  styleUrls: ["./invoice-list.component.scss"],
})
export class InvoiceListComponent implements OnInit, AfterViewInit {
  public loading = false;
  public refresh = new Subject<void>();

  public data: CustomerInvoice[] = [];
  public pages = 0;

  public proceda: any[] = [];

  /** Elegibilidade para a liberação por comprovante (XD-2207); null até a consulta responder. */
  public temporaryRelease: TemporaryReleaseEligibility = null;
  public temporaryReleaseGranted = false;
  private paymentProofRequestedHandled = false;

  public displayedColumns = [
    "numero",
    "id",
    "value",
    "data_hora_criacao",
    "data_vencimento",
    "data_pagamento",
    "status",
    "actions",
  ];

  public searchParameters: InvoiceSearchParameters = {
    limit: 10,
    offset: 0,
    situacao: `1,2,3,4,5,7,9`,
  };
  public filterParameters: InvoiceSearchParameters = {};
  public filters: ListingSearchFilter[] = [
    {
      value: "numero",
      viewValue: "Número da fatura",
      displayType: ListingSearchType.Text,
      placeholder: "Número da fatura",
    },
    {
      value: "dataCriacao",
      viewValue: "Data de criação",
      displayType: ListingSearchType.DateRange,
      placeholder: "Data de criação",
    },
    {
      value: "dataVencimento",
      viewValue: "Data de vencimento",
      displayType: ListingSearchType.DateRange,
      placeholder: "Data de vencimento",
    },
    {
      value: "dataPagamento",
      viewValue: "Data de pagamento",
      displayType: ListingSearchType.DateRange,
      placeholder: "Data de pagamento",
    },
  ];

  @ViewChild(MatSort) private sort: MatSort;
  @ViewChild(ListingComponent) private listing: ListingComponent;

  public categoriasNotasFiscais = [
    { codigoServico: 3115, descricao: "NFe Transporte" },
    { codigoServico: 2800, descricao: "NFe Tecnologia" },
  ];

  private URL_FATURAS_CLIENTE: string =
    environment.angularJSconstants.URL_SERVICO_FISCAL;

  constructor(
    private customerApi: CustomerApiService,
    private matSnackBar: MatSnackBar,
    private tour: TourService,
    private router: UiRouterState,
    private stateParams: StateParamsService,
    @Inject(HttpClient) protected http: HttpClient,
    private profile: PerfilService,
    private financialApi: Financeiro,
    private localStorageService: LocalStorageService,
    private dialog: MatDialog,
    private temporaryReleaseService: TemporaryReleaseService
  ) {}

  ngOnInit() {
    this.sort.sortChange.subscribe(() => {
      this.listing.currentPage = 1;
      this.searchParameters.sort =
        (this.sort.direction === "asc" ? "" : "-") + this.sort.active;
    });

    this.listing.currentPageChange.subscribe((page) => {
      this.searchParameters.offset = (page - 1) * this.listing.currentLimit;

      this.refresh.next();
    });
    this.listing.currentLimitChange.subscribe((limit) => {
      this.searchParameters.limit = limit;
      this.searchParameters.offset = 0;

      this.refresh.next();
    });

    merge(this.refresh, this.sort.sortChange)
      .pipe(
        startWith({}),
        tap(() => (this.loading = true)),
        switchMap(() =>
          this.getFaturasCliente(this.searchParameters, this.filterParameters)
        ),
        catchError(() => observableOf({ data: [], total: 0 })),
        tap((response) => {
          this.data = response.data;
          this.pages = Math.ceil(response.total / this.searchParameters.limit);
          this.loading = false;
        })
      )
      .subscribe();

    this.verificaArquivosProceda();
    this.loadTemporaryReleaseEligibility();
  }

  /** A mesma regra é reavaliada no envio: esconder o botão aqui é só UX, não proteção. */
  public loadTemporaryReleaseEligibility() {
    this.temporaryReleaseService.getEligibility().subscribe(
      (eligibility) => {
        this.temporaryRelease = eligibility;
        this.openPaymentProofDialogFromSuspendedModal();
      },
      () => (this.temporaryRelease = null)
    );
  }

  /**
   * O "Já paguei esta fatura" do modal "Serviço suspenso" (AngularJS, coleta e reversas) traz o
   * seller para cá com ?comprovante, e o envio usa o mesmo modal da listagem. Abre uma vez só.
   */
  private openPaymentProofDialogFromSuspendedModal() {
    if (!this.stateParams.comprovante || this.paymentProofRequestedHandled) {
      return;
    }
    this.paymentProofRequestedHandled = true;
    if (this.temporaryRelease && this.temporaryRelease.eligible) {
      this.openPaymentProofDialog();
    }
  }

  public canSendPaymentProof(invoice: CustomerInvoice): boolean {
    return !!(
      this.temporaryRelease &&
      this.temporaryRelease.eligible &&
      invoice &&
      invoice.id === this.temporaryRelease.invoiceId
    );
  }

  public openPaymentProofDialog() {
    this.dialog
      .open(PaymentProofDialogComponent, { autoFocus: false })
      .afterClosed()
      .subscribe((result: TemporaryReleaseResult) => {
        if (!result) {
          return;
        }
        this.temporaryReleaseGranted = true;
        this.temporaryRelease = null;
        this.loadTemporaryReleaseEligibility();
      });
  }

  public getFaturasCliente(searchParams: any, filterParams: any): Observable<any> {
    const user = this.localStorageService.get("user");

    const params = new HttpParams({ fromObject: { ...searchParams, ...filterParams } });

    return this.http.get(
      `${this.URL_FATURAS_CLIENTE}/v2/faturas`,
      { params, responseType: 'json' }
    );
  }

  ngAfterViewInit() {
    if (this.stateParams.tour) {
      this.startTour();
    }
  }

  isEmptyDatasource() {
    return !this.data || this.data.length === 0;
  }

  getInvoiceStatusClass(invoice) {
    const invoiceStatus = InvoiceStatus.fromValue<InvoiceStatus>(
      invoice.situacao
    );
    let status = "";

    switch (invoiceStatus.code) {
      case "Paga":
        status = "status-success";
        break;
      case "Vencida":
        status = "status-danger";
        break;
      case "NaoPaga":
        status = "status-info";
        break;
      case "Reembolsada":
        status = "status-warning";
        break;
    }

    return "tag " + status;
  }

  applySearch(searchData) {
    this.filterParameters = {};
    if (searchData.value) {
      this.filterParameters[searchData.searchBy] = searchData.value;
    }
    if (searchData.valueStart) {
      this.filterParameters[searchData.searchBy + "Inicial"] =
        searchData.valueStart;
    }
    if (searchData.valueEnd) {
      this.filterParameters[searchData.searchBy + "Final"] =
        searchData.valueEnd;
    }

    this.refresh.next();
  }

  public async verificaArquivosProceda() {
    const user = JSON.parse(localStorage.getItem("ls.user")) || null;
    if (user) {
      this.proceda = JSON.parse(await this.financialApi.checkTypes(user.id));
    }
  }

  public async downloadDoccob(invoice: any) {
    try {
      this.matSnackBar.open(`Aguarde estamos buscando o arquivo`, "", {
        duration: 5000,
      });
      const data = await this.financialApi.getDoccob(invoice.id);
      window.open(data, "_blank");
    } catch (error) {
      this.matSnackBar.open(
        `Não foi possível baixar o arquivo Doccob solicitado desta fatura`,
        "",
        { duration: 5000 }
      );
    }
  }

  public async downloadConemb(invoice: any) {
    try {
      this.matSnackBar.open(`Aguarde estamos buscando o arquivo`, "", {
        duration: 5000,
      });
      const data = await this.financialApi.getConemb(invoice.id);
      window.open(data, "_blank");
    } catch (error) {
      this.matSnackBar.open(
        `Não foi possível baixar o arquivo Conemb solicitado desta fatura`,
        "",
        { duration: 5000 }
      );
    }
  }

  public urlNotaFiscal(notaFiscal) {
    if (notaFiscal) {

      if (notaFiscal.urlNfse !== null && notaFiscal.urlNfse !== "") {
        const identificadorNfse = notaFiscal.urlNfse.split('?')[0].split('/').pop();
        this.http.get(
          `${environment.angularJSconstants.NEW_URL_API_DOCUMENTS}/v2/nfse/download/${identificadorNfse}`,
          { responseType: 'arraybuffer', observe: 'response' }
        ).subscribe(
          (response) => {
            const data = response.body;
            const mime = response.headers.get('content-type') || 'application/pdf';
            downloadFile(data, mime, `nota_fiscal_${notaFiscal.numeroNfse}.pdf`);
          },
          () => {
            this.matSnackBar.open('Não foi possível baixar a nota fiscal', '', { duration: 3000 });
          }
        );
      } else {
        const urlNotasPefeitura = "https://nfe.prefeitura.sp.gov.br/contribuinte/notaprint.aspx";
        window.open(
          `${urlNotasPefeitura}?ccm=49823388&nf=${notaFiscal.numeroNfse}&cod=${notaFiscal.codigoVerificacao}`,
          "_blank"
        );
      }
    } else {
      return null;
    }
  }

  public urlBoleto(fatura) {
    if (fatura) {
      this.http.get(
        `${environment.angularJSconstants.NEW_URL_API_DOCUMENTS}/v2/faturas/${fatura.id}/boletos`,
        { responseType: 'arraybuffer', observe: 'response' }
      ).subscribe(
        (response) => {
          const data = response.body;
          const mime = response.headers.get('content-type') || 'application/pdf';
          downloadFile(data, mime, `boleto_${fatura.id}.pdf`);
        },
        () => {
          this.matSnackBar.open('Não foi possível baixar o boleto desta fatura', '', { duration: 3000 });
        }
      );
    }
  }

  private mostrarSnackBar(mensagem: string) {
    this.matSnackBar.open(mensagem, '', { duration: 3000 });
  }

  public checkNotaFiscal(notasFiscais, categoria) {
    const notaFiscal = notasFiscais.find(
      (nota) =>
        nota.codigoServico === categoria.codigoServico &&
        nota.numeroNfse &&
        nota.codigoVerificacao
    );

    if (notaFiscal) {
      return true;
    }

    return false;
  }

  async downloadInvoiceFile(invoice: CustomerInvoice, format: string) {

    const comparisonDate = new Date('2024-09-17');
    const invoiceDate = new Date(invoice.dataHoraCriacao);
    if (invoiceDate < comparisonDate) {
      this.customerApi
        .getUserInvoiceFile(invoice.id, format)
        .pipe(
          tap(
            (result) => {
              const data = result.body;
              const mime = result.headers.get("content-type");
              const filename = `fatura_${invoice.numero}.${format}`;
              downloadFile(data, mime, filename);
            },
            () => {
              this.matSnackBar.open(
                `Não foi possível encontrar o arquivo ${format.toLocaleUpperCase()} desta fatura`,
                "",
                { duration: 3000 }
              );
            }
          )
        )
        .subscribe();
    } else {
      if (format === 'pdf') {
        this.http.get(
          `${environment.angularJSconstants.NEW_URL_API_DOCUMENTS}/v2/faturas/${invoice.id}/sumarizada`,
          {responseType: 'arraybuffer', observe: 'response'}
        ).subscribe(
          (response) => {
            const data = response.body;
            const mime = response.headers.get('content-type') || 'application/pdf';
            downloadFile(data, mime, `fatura_${invoice.numero}.pdf`);
          },
          () => {
            this.matSnackBar.open(
              'Não foi possível encontrar o arquivo PDF desta fatura', '', {duration: 3000}
            );
          }
        );
      }

      if (format === 'csv') {
        this.http.get<any>(
          `${environment.angularJSconstants.NEW_URL_API_DOCUMENTS}/v2/faturas/${invoice.id}/arquivos/url-assinada`
        ).subscribe(
          (response) => {
            window.open(response.dto.detailedBillingLink, '_blank');
          },
          () => {
            this.matSnackBar.open(
              'Não foi possível encontrar o arquivo CSV desta fatura', '', {duration: 3000}
            );
          }
        );
      }
    }
  }

  private startTour() {
    const steps = this.tour.makeTour([
      {
        title: "Veja suas faturas",
        text: "Tenha acesso às suas faturas, acompanhe a data de vencimento e faça o download das faturas detalhadas e simplificadas.",
        attachTarget: () =>
          document.querySelector<HTMLElement>(
            "app-toolbar .mat-tab-label-active"
          ),
        previous: {
          text: "Voltar",
          action: (ev, ref) => {
            ref.close("to_previous_section");
            this.router.go("dashboard", { tour: true });
          },
        },
      },
      {
        title: "Faça download das faturas",
        text:
          "Clique aqui para baixar a fatura simplificada em PDF, fatura detalhada em CSV e boletos. " +
          "Caso tenha dúvidas em visualizar sua fatura detalhada, " +
          '<a href="https://mandaenuvemenvio.zendesk.com/hc/pt-br/articles/34308958017556-Como-consultar-minha-fatura-de-forma-detalhada"' +
          'target="_blank">clique aqui</a>.',
        attachTarget: () =>
          document.querySelector<HTMLElement>("td.mat-column-actions") ||
          document.querySelector<HTMLElement>("th.mat-column-actions"),
        next: {
          text: "Ir para Reembolso",
          action: (ev, ref) => {
            ref.close("to_next_section");
            this.router.go("reembolsos", { tour: true });
          },
        },
      },
    ]);

    this.tour.start(steps).subscribe();
  }

  public verificarPorStatus(situacao: number): boolean {
    if (situacao === 6 || situacao === 8) {
      return false;
    }

    return true;
  }

  public async baixarArquivo(tipo, invoice) {
    try {
      this.matSnackBar.open(`Aguarde estamos buscando o arquivo`, "", {
        duration: 5000,
      });
      const data = await this.financialApi.getDocumentoFiscal(tipo, invoice.id);
      if (data) {
        this.matSnackBar.dismiss();

        // Create an invisible A element
        const a = document.createElement("a");
        a.style.display = "none";
        document.body.appendChild(a);

        a.href = data.url;
        a.click();

        window.URL.revokeObjectURL(a.href);
        document.body.removeChild(a);
      } else {
        throw null;
      }
    } catch (error) {
      this.matSnackBar.open(
        `Não foi possível baixar o arquivo solicitado desta fatura`,
        "",
        { duration: 5000 }
      );
    }
  }
}
